/* ============================================================
   <reading-progress> — sticky scroll-progress bar for articles.

   Best practices applied:
   - Single passive scroll listener, throttled to one update per frame
     via requestAnimationFrame (no layout thrash).
   - Animates a transform: scaleX value, NOT width — keeps the work on
     the compositor thread.
   - Tracks a configurable target element (the article body), not the
     whole page. Progress maps from "top of article enters viewport"
     to "bottom of article reaches viewport bottom".
   - From 1280px the bar is a pill under the header with the title, a
     round track and the percent (assets/article.css), so the reader
     keeps context on a long page; below, a slim track.
   - Hides when the related-articles block enters view.
   - Aborts listeners cleanly on disconnect via AbortController.
   - role="progressbar" + aria-valuenow/min/max for assistive tech.
   ============================================================ */

if (!customElements.get('reading-progress')) {
  customElements.define('reading-progress', class ReadingProgress extends HTMLElement {
    connectedCallback() {
      this.setAttribute('role', 'progressbar');
      this.setAttribute('aria-valuemin', '0');
      this.setAttribute('aria-valuemax', '100');
      this.setAttribute('aria-valuenow', '0');
      this.setAttribute('aria-label', this.getAttribute('aria-label') || 'Progression de lecture');

      this.bar = this.querySelector('.reading-progress__bar');
      if (!this.bar) return;

      this.target = document.querySelector(this.getAttribute('data-target') || '.article .rte');
      if (!this.target) return;

      this.pctEl = this.querySelector('.reading-progress__pct');
      this.related = document.querySelector('.article-related');

      if (!this.querySelector('.reading-progress__meta')) {
        const titleText = this.getAttribute('data-title') || '';
        if (titleText) {
          const meta = document.createElement('span');
          meta.className = 'reading-progress__meta';
          meta.setAttribute('aria-hidden', 'true');
          const title = document.createElement('span');
          title.className = 'reading-progress__title';
          title.textContent = titleText;
          const pct = document.createElement('span');
          pct.className = 'reading-progress__pct';
          meta.appendChild(title);
          meta.appendChild(pct);
          this.appendChild(meta);
          this.pctEl = pct;
        }
      }

      this.controller = new AbortController();
      const opts = { passive: true, signal: this.controller.signal };
      this.ticking = false;
      this.handleScroll = this.onScroll.bind(this);

      window.addEventListener('scroll', this.handleScroll, opts);
      window.addEventListener('resize', this.handleScroll, opts);
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
    }

    updateHeaderOffset() {
      const header = document.querySelector('.header-section') || document.querySelector('.header');
      const bottom = header ? Math.max(header.getBoundingClientRect().bottom, 0) : 0;

      document.documentElement.style.setProperty('--reading-progress-top', `${Math.round(bottom)}px`);
    }

    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        this.update();
        this.ticking = false;
      });
    }

    update() {
      this.updateHeaderOffset();

      const rect = this.target.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const scrolled = Math.min(Math.max(-rect.top, 0), Math.max(rect.height, 1));
      const denom = Math.max(rect.height - viewportH, 1);
      const ratio = Math.min(Math.max(scrolled / denom, 0), 1);
      const percent = Math.round(ratio * 100);

      this.bar.style.transform = `scaleX(${ratio})`;
      this.setAttribute('aria-valuenow', String(percent));
      if (this.pctEl) this.pctEl.textContent = percent + '%';

      const relatedInView = this.related && this.related.getBoundingClientRect().top < viewportH * 0.88;
      const reading = ratio > 0 && ratio < 1 && !relatedInView;
      this.classList.toggle('is-active', reading);
    }
  });
}
